/**
 * Normalize AI-generated blocks to match the ContentBlock schema expected by PageEditor.
 *
 * AI returns:  { type, blockName, content: { headline, items, ... }, styles, settings }
 * Editor needs: { blockType, blockName, content: { headline, features, faqs, ... }, styles, ... }
 *
 * Key differences:
 * - "type" → "blockType"
 * - content.items → content.features / content.testimonials / content.faqs (per block type)
 * - CTA: content.headline → content.title
 * - Features items need { id, title, description, icon }
 * - Testimonials need { id, quote, author, role, company }
 * - FAQ needs { id, question, answer }
 */

import type { ContentBlock, BlockType, BlockContent } from '../types';

let _counter = 0;
function uid() { return `ai-${Date.now()}-${_counter++}`; }

export function normalizeAIBlocks(blocks: any[], pageId: string = ''): ContentBlock[] {
  if (!Array.isArray(blocks)) return [];

  return blocks.map((raw, i) => {
    const blockType = (raw.type || raw.blockType || 'text') as BlockType;
    const content = normalizeContent(blockType, raw.content || {});
    const styles = raw.styles || null;
    const settings = raw.settings || null;

    return {
      id: raw.id || uid(),
      pageId,
      parentBlockId: null,
      blockType,
      blockName: raw.blockName || raw.block_name || `${blockType} ${i + 1}`,
      content,
      styles,
      settings,
      sortOrder: raw.sortOrder ?? i,
      isVisible: raw.isVisible ?? true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });
}

function normalizeContent(blockType: BlockType, c: any): BlockContent {
  switch (blockType) {
    case 'hero':
      return {
        headline: c.headline || c.title || '',
        subheadline: c.subheadline || c.subtitle || c.description || '',
        backgroundImage: c.backgroundImage || c.imageUrl || c.background_image || c.backgroundImageUrl,
        // Backend build_page writes a single `cta_primary: {text,url}`; older AI output uses
        // ctaButtons/buttons/cta. normalizeButtons handles both the object and array forms.
        ctaButtons: normalizeButtons(c.ctaButtons || c.buttons || c.cta || c.cta_primary),
      };

    case 'cta':
      return {
        title: c.title || c.headline || '',
        description: c.description || c.subheadline || c.text || '',
        // Backend build_page writes flat snake_case button_text/button_url; support that
        // alongside camelCase buttonText/buttonUrl and pre-built button arrays.
        buttons: normalizeButtons(
          c.buttons ||
            c.ctaButtons ||
            ((c.buttonText || c.button_text)
              ? [{ text: c.buttonText || c.button_text, url: c.buttonUrl || c.button_url || '#', style: 'primary' }]
              : []),
        ),
      };

    case 'features':
      return {
        features: (c.features || c.items || []).map((f: any, i: number) => ({
          id: f.id || uid(),
          title: f.title || f.name || '',
          description: f.description || '',
          icon: f.icon || '⚡',
        })),
      };

    case 'testimonials':
      return {
        testimonials: (c.testimonials || c.items || []).map((t: any) => ({
          id: t.id || uid(),
          quote: t.quote || t.text || t.content || '',
          author: t.author || t.name || '',
          role: t.role || t.title || '',
          company: t.company || t.organization || '',
          rating: t.rating || 5,
        })),
      };

    case 'faq':
      return {
        faqs: (c.faqs || c.questions || c.items || []).map((q: any) => ({
          id: q.id || uid(),
          question: q.question || q.title || '',
          answer: q.answer || q.description || q.content || '',
        })),
      };

    case 'stats':
      // Stats aren't a native PageEditor block — render as features
      return {
        features: (c.stats || c.items || []).map((s: any) => ({
          id: s.id || uid(),
          title: s.value || s.number || s.stat || '',
          description: s.label || s.title || s.description || '',
          icon: s.icon || '📊',
        })),
      };

    case 'pricing':
      return {
        plans: (c.plans || c.items || []).map((p: any) => ({
          id: p.id || uid(),
          name: p.name || p.title || '',
          price: p.price || '',
          period: p.period || '/month',
          features: Array.isArray(p.features) ? p.features : [],
          ctaText: p.ctaText || p.buttonText || 'Get Started',
          ctaUrl: p.ctaUrl || p.buttonUrl || '#',
          isPopular: p.isPopular || p.popular || false,
        })),
      };

    case 'text':
    case 'richtext':
      // Backend build_page writes the body as HTML under `content`; the Craft renderer shows
      // `richText` as HTML (falling back to a plain <p>{text}</p>). Route `content`/`body`
      // into richText so the body isn't rendered as escaped text (or dropped entirely).
      return {
        text: c.text || c.body || c.content || '',
        richText: c.richText || c.html || c.content || c.body || (c.text ? `<p>${c.text}</p>` : ''),
        headline: c.headline || c.title,
      };

    case 'image':
      return {
        imageUrl: c.imageUrl || c.url || c.src || '',
        imageAlt: c.imageAlt || c.alt || c.description || '',
        caption: c.caption,
      };

    case 'form':
      return {
        title: c.title || c.headline || 'Contact Us',
        description: c.description,
        formId: c.formId || c.form_id,
      };

    case 'contact':
      return {
        title: c.title || c.headline || 'Contact Us',
        description: c.description,
        address: c.address,
        phone: c.phone,
        email: c.email,
        hours: c.hours,
      };

    default:
      // Pass through as-is for unknown types, with basic normalization
      return {
        ...c,
        title: c.title || c.headline,
        text: c.text || c.body || c.description,
      };
  }
}

function normalizeButtons(raw: any): Array<{ id: string; text: string; url: string; style: string }> {
  if (!raw) return [{ id: uid(), text: 'Get Started', url: '#', style: 'primary' }];
  if (!Array.isArray(raw)) {
    if (typeof raw === 'object') return [{ id: uid(), text: raw.text || 'Click', url: raw.url || '#', style: raw.style || 'primary' }];
    return [{ id: uid(), text: String(raw), url: '#', style: 'primary' }];
  }
  return raw.map((b: any) => ({
    id: b.id || uid(),
    text: b.text || b.label || 'Click',
    url: b.url || b.href || '#',
    style: b.style || b.variant || 'primary',
  }));
}
